import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Login as LoginIcon } from '@mui/icons-material';
import { checkUserExists } from '../services/firebase';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  // State for user information
  const [email, setEmail] = useState<string>('');
  
  // State for UI
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
  }>({});
  
  // Auth context
  const { sendMagicLink } = useAuth();
  
  // Auto-login for test account
  useEffect(() => {
    const autoLogin = async () => {
      // Check if we're auto-logging in from localStorage flag
      const autoLoginFlag = localStorage.getItem('autoLoginTestAccount');
      if (autoLoginFlag === 'true') {
        localStorage.removeItem('autoLoginTestAccount');
        // Redirect to main page - the auth context will handle the actual login
        navigate('/');
      }
    };
    
    autoLogin();
  }, [navigate]);
  
  // Handle email change
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setFieldErrors({ ...fieldErrors, email: undefined });
  };
  
  // Validate form
  const validateForm = (): boolean => {
    const errors: {
      email?: string;
    } = {};
    
    // Validate email
    if (!email.trim()) {
      errors.email = 'Please enter your email address';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    setFieldErrors(errors);
    
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (loading) return;
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Special case for Razorpay test account
      if (email.toLowerCase() === 'testervip@ut.com') {
        // Set flag for auto-login and trigger page reload
        localStorage.setItem('testAccountLogin', 'true');
        localStorage.setItem('autoLoginTestAccount', 'true');
        
        // Show success message
        setError(null);
        setLoading(false);
        
        // Show a message and redirect
        setError('Logging in as test account...');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return;
      }
      
      // Check if user exists
      const userExists = await checkUserExists(email);
      
      if (!userExists) {
        // User doesn't exist, redirect to signup
        setError('No account found with this email. Please sign up first.');
        setTimeout(() => {
          navigate('/signup', { state: { email } });
        }, 2000);
      } else {
        // User exists, send magic link
        await sendMagicLink(email);
        setEmailSent(true);
      }
    } catch (err) {
      console.error('Error sending magic link:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: 4,
          mb: 4,
          borderRadius: 2
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Welcome Back
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Sign in to access your capsules and premium features
          </Typography>
        </Box>
        
        {emailSent ? (
          <Box sx={{ textAlign: 'center' }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              Magic link sent! Check your email to sign in.
            </Alert>
            
            <Typography variant="body1" paragraph>
              We've sent a sign-in link to <strong>{email}</strong>. Click the link in the email to sign in to your account.
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              The link will expire in 15 minutes. If you don't see the email, check your spam folder.
            </Typography>
            
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setEmailSent(false)}
              sx={{ mt: 2, borderRadius: 4 }}
            >
              Use a different email
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              variant="outlined"
              type="email"
              value={email}
              onChange={handleEmailChange}
              disabled={loading}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              sx={{ mb: 4 }}
              placeholder="Enter your email address"
              autoFocus
            />
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={loading}
              startIcon={!loading && <LoginIcon />}
              sx={{ 
                py: 1.5,
                borderRadius: 4
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Login with Magic Link'
              )}
            </Button>
            
            {error && (
              <Alert severity={error.includes('Logging in as test account') ? 'success' : 'error'} sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                We'll email you a magic link for password-free sign in.
              </Typography>
            </Box>
          </form>
        )}
        
        <Divider sx={{ my: 4 }} />
        
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Don't have an account yet?
          </Typography>
          
          <Button
            component={Link}
            to="/signup"
            variant="outlined"
            color="primary"
            sx={{ mt: 2, borderRadius: 4, px: 4 }}
          >
            Sign Up
          </Button>
          
          <Box sx={{ mt: 3 }}>
            <Button
              component={Link}
              to="/"
              variant="text"
              color="primary"
              size="small"
            >
              Back to Home
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;
